/**
 * Wanderlust Jewelry - sizing tools.
 * Plain custom elements with no imports from Horizon so theme upgrades cannot break them.
 *
 *   <wj-tabs>             accessible tablist (arrow keys, Home/End)
 *   <wj-size-calc>        ruler measurement in mm -> ring size, and selects it on the product
 *   <wj-custom-size>      "Keep size P / Resize to my size" order choice
 *   <wj-necklace-lengths> highlights a chain length on the silhouette
 */

class WjTabs extends HTMLElement {
  connectedCallback() {
    // Re-connection (e.g. the theme editor moving a section) must not double the listeners: arrow keys would
    // then jump two tabs.
    if (this.ready) return;
    this.ready = true;
    this.tabs = [...this.querySelectorAll(':scope > .wj-tabs__list [role="tab"]')];
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.tab));
      tab.addEventListener('keydown', (event) => this.#onKeydown(event));
    });
  }

  /** @param {string} name */
  select(name, focus = false) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.tab === name;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });
    this.querySelectorAll(':scope > [role="tabpanel"]').forEach((panel) => {
      panel.hidden = panel.dataset.panel !== name;
    });
    this.dataset.active = name;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    const index = this.tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    const last = this.tabs.length - 1;
    const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const wrapped = next > last ? 0 : next < 0 ? last : next;
    this.select(this.tabs[wrapped].dataset.tab, true);
  }
}

/* ---------- shared size helpers (used by the calculator and the on-screen sizer) ---------- */

/** @param {Array<{d:number}>} sizes @param {number} diameter */
function nearest(sizes, diameter) {
  return sizes.reduce((best, size) => (Math.abs(size.d - diameter) < Math.abs(best.d - diameter) ? size : best));
}

/** The resize option on this product page, if the product is a resizable ring. */
function resizeOption() {
  const el = document.querySelector('wj-custom-size');
  return el && typeof el.resizeTo === 'function' ? el : null;
}

/**
 * Finds the size option on the product form that matches a ring size, whatever label the product uses.
 * Handles the labels found on the WooCommerce catalogue (docs/research/01): "P (18mm)", "R.5 (19mm)", "18mm",
 * plus "N½", "N 1/2", "UK N½", "Size N", "US 7".
 */
function findVariantInput(size) {
  const normalise = (value) =>
    String(value)
      .toLowerCase()
      .replace(/\(.*?\)/g, '')
      .replace(/^\s*(uk|za|us|size|eu)[\s:]*/, '')
      .replace(/\s*1\/2/, '½')
      .replace(/\.5\b/, '½')
      .replace(/\s+/g, '');
  const wanted = new Set([size.uk, `US ${size.us}`, size.eu].map(normalise));
  const mmOnly = (value) => {
    // "17.25mm", "17.50mm" and the catalogue typo "17.50m"
    const m = String(value).trim().match(/^(\d{2}(?:\.\d+)?)\s*mm?$/i);
    return m ? Math.abs(Number(m[1]) - size.d) <= 0.15 : false;
  };
  const matches = (value) => wanted.has(normalise(value)) || mmOnly(value);

  for (const picker of document.querySelectorAll('variant-picker')) {
    for (const input of picker.querySelectorAll('input[type="radio"]')) {
      if (matches(input.value)) return input;
    }
    for (const option of picker.querySelectorAll('select option')) {
      if (matches(option.value)) return option;
    }
  }
  return null;
}

/** What the "select" button should say for a size, or null when the page can't act on it. */
function selectLabel(size) {
  if (findVariantInput(size)) return `Select size ${size.uk}`;
  if (resizeOption()) return `Order resized to ${size.uk}`;
  return null;
}

/** Selects the size on the product: the in-stock variant if there is one, otherwise "Resize to my size". */
function selectSize(source, size) {
  const match = findVariantInput(size);
  if (match instanceof HTMLOptionElement) {
    const select = /** @type {HTMLSelectElement} */ (match.closest('select'));
    select.value = match.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    resizeOption()?.keep();
  } else if (match) {
    match.click();
    resizeOption()?.keep();
  } else {
    const resize = resizeOption();
    if (!resize) return;
    resize.resizeTo(size.uk);
  }
  source.dispatchEvent(new CustomEvent('wj:size-selected', { bubbles: true, detail: size }));
  source.closest('dialog')?.close();
}

/**
 * Size calculator: the customer types a ruler measurement in mm (inside diameter of a ring, or the length of a
 * paper strip around the finger) and gets the nearest size from the house table.
 */
class WjSizeCalc extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.sizes = JSON.parse(this.dataset.sizes || '[]');
    const $ = (name) => this.querySelector(`[data-ref="${name}"]`);
    this.mm = $('mm');
    this.kind = $('kind');
    this.result = $('result');
    this.button = $('select');
    this.mm.addEventListener('input', () => this.update());
    this.kind.addEventListener('change', () => this.update());
    this.button.addEventListener('click', () => this.size && selectSize(this, this.size));
  }

  update() {
    const value = Number(String(this.mm.value).replace(',', '.'));
    this.size = null;
    this.button.hidden = true;
    if (!value) {
      this.result.textContent = '';
      return;
    }
    const diameter = this.kind.value === 'c' ? value / Math.PI : value;
    const first = this.sizes[0];
    const last = this.sizes.at(-1);
    if (diameter < first.d - 0.4 || diameter > last.d + 0.4) {
      this.result.textContent = `That's outside our size range (${first.uk} to ${last.uk}). Please measure again, or contact us.`;
      return;
    }
    const size = nearest(this.sizes, diameter);
    this.size = size;
    this.result.innerHTML = `Your size: <strong>${size.uk}</strong> <span>US ${size.us} · EU ${size.eu} · inside diameter ${size.d} mm</span>`;
    if (this.hasAttribute('data-select-size')) {
      const label = selectLabel(size);
      if (label) {
        this.button.textContent = label;
        this.button.hidden = false;
      }
    }
  }
}

/**
 * "Keep size P / Resize to my size" (blocks/wj-custom-size.liquid).
 * Resizing is an order choice, not a variant (decision D-06): the ring's variant stays its real, in-stock size, and
 * the chosen size travels on the line item as properties[Resize to] + properties[Resize from]. Both inputs are
 * disabled (so they post nothing) unless "Resize" is chosen, and the size list is required while it is.
 * Fires `wj:resize-change` on document so the delivery estimate can add the resize lead time.
 * Paid resizing (D-77, D-82): unless the ring is priced over the free-resize threshold, choosing a size adds the
 * ring-resize fee (RESIZE-UP-4 larger / RESIZE-DOWN-4 smaller; live variant ids and prices in data-fee-up /
 * data-fee-down) through assets/wj-fee-lines.js, with the shared hidden "_Fee ref". The price shows as it's chosen.
 */
class WjCustomSize extends HTMLElement {
  connectedCallback() {
    if (!this.ready) {
      this.ready = true;
      const $ = (name) => this.querySelector(`[data-ref="${name}"]`);
      this.keepInput = /** @type {HTMLInputElement} */ ($('keep'));
      this.resizeInput = /** @type {HTMLInputElement} */ ($('resize'));
      this.fields = $('fields');
      this.select = /** @type {HTMLSelectElement} */ ($('select'));
      this.from = /** @type {HTMLInputElement} */ ($('from'));
      this.keepLabel = $('keepLabel');
      this.priceNote = $('priceNote');
      this.feeOut = $('fee');
      this.formId = this.select.getAttribute('form') || '';
      const json = (value) => {
        try {
          return value ? JSON.parse(value) : null;
        } catch {
          return null;
        }
      };
      this.fees = { up: json(this.dataset.feeUp), down: json(this.dataset.feeDown) };
      this.price = Number(this.dataset.price) || 0;
      this.order = [];
      if (this.dataset.calc) {
        import(this.dataset.calc).then((calc) => {
          this.calc = calc;
          this.showFee();
        });
      }

      for (const size of JSON.parse(this.dataset.sizes || '[]')) {
        this.select.add(new Option(`${size.uk}  ·  US ${size.us}  ·  ${size.d} mm`, size.uk));
        this.order.push(size.uk);
      }

      this.addEventListener('change', (event) => {
        if (event.target === this.keepInput || event.target === this.resizeInput) this.sync();
        if (event.target === this.select) this.showFee();
      });
      // A variant at another price can cross the free-resize threshold (Horizon: event.promise -> variant JSON).
      this.onSelect = (event) => {
        this.onVariant();
        const promise = /** @type {any} */ (event).promise;
        if (!promise?.then) return;
        promise
          .then((result) => {
            const price = Number(result?.detail?.resource?.price);
            if (price > 0) {
              this.price = price;
              this.showFee();
            }
          })
          .catch(() => {});
      };
      // Horizon re-renders the variant picker after a change; read the new size once it has.
      this.onVariant = () => window.setTimeout(() => this.updateCurrentSize(), 0);
    }

    document.addEventListener('change', this.onVariant);
    document.addEventListener('shopify:product:select', this.onSelect);
    (window.WJFeeProviders ||= new Set()).add(this);
    this.updateCurrentSize();
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onVariant);
    document.removeEventListener('shopify:product:select', this.onSelect);
    window.WJFeeProviders?.delete(this);
  }

  /** Free first resize on this ring at its current price after any Black November discount? Strictly over the threshold (D-77, D-158). */
  get free() {
    if (!this.calc) return false;
    const price = (window.wjBnPrice || ((c) => c))(this.price, this.dataset.wjBnPcts);
    return this.calc.resizeIsFree(price, this.dataset.freeMin);
  }

  /** The fee for the chosen size, or null (free, keeping the size, same size, unknown size, or no fee product). */
  fee() {
    if (!this.resizing || !this.calc || this.free) return null;
    const direction = this.calc.resizeDirection(this.from.value, this.select.value, this.order);
    const fee = direction ? this.fees[direction] : null;
    return fee ? { direction, ...fee } : null;
  }

  showFee() {
    if (!this.calc) return;
    if (this.priceNote) this.priceNote.textContent = (this.free ? this.dataset.freeNote : this.dataset.paidNote) || '';
    if (!this.feeOut) return;
    const from = this.from.value;
    const to = this.select.value;
    if (!this.resizing || !to) {
      this.feeOut.textContent = '';
    } else if (to === from) {
      this.feeOut.textContent = `This ring is already size ${to}: choose "Keep size ${to}".`;
    } else if (this.free) {
      this.feeOut.textContent = `Resize ${from} to ${to}: ${this.dataset.freeNote || ''}`;
    } else {
      const fee = this.fee();
      this.feeOut.textContent = fee
        ? `Resize ${from} to ${to} (${fee.direction === 'up' ? 'larger' : 'smaller'}): ${this.calc.formatMoney(fee.price)}`
        : '';
    }
  }

  /**
   * Fee line for /cart/add.js, asked for by assets/wj-fee-lines.js just before the ring goes into the bag.
   * @param {{ ref: string, quantity: number }} context
   */
  feeItems({ ref, quantity }) {
    const fee = this.fee();
    if (!fee?.id) return [];
    return [{ id: fee.id, quantity, properties: { '_Fee ref': ref } }];
  }

  /** @param {string} message */
  feeError(message) {
    if (this.feeOut) this.feeOut.textContent = message;
  }

  /** The ring's current (in-stock) size, read from the selected variant. */
  currentSize() {
    const option = this.dataset.sizeOption;
    if (option) {
      for (const input of document.querySelectorAll('variant-picker input[type="radio"]:checked')) {
        if (/** @type {HTMLInputElement} */ (input).dataset.optionName === option) return input.value;
      }
      for (const select of document.querySelectorAll('variant-picker select')) {
        if (/** @type {HTMLSelectElement} */ (select).name === `options[${option}]`) return select.value;
      }
    }
    return this.from.value;
  }

  updateCurrentSize() {
    const size = this.currentSize();
    if (!size) return;
    this.from.value = size;
    this.keepLabel.textContent = `Keep size ${size}`;
    this.showFee();
  }

  get resizing() {
    return this.resizeInput.checked;
  }

  sync() {
    const on = this.resizing;
    this.fields.hidden = !on;
    this.select.disabled = !on;
    this.select.required = on;
    this.from.disabled = !on;
    if (on) {
      this.select.name = 'properties[Resize to]';
      this.from.name = 'properties[Resize from]';
    } else {
      this.select.removeAttribute('name');
      this.from.removeAttribute('name');
    }
    document.dispatchEvent(
      new CustomEvent('wj:resize-change', { detail: { on, leadDays: Number(this.dataset.leadDays) || 0 } })
    );
    this.showFee();
  }

  keep() {
    this.keepInput.checked = true;
    this.sync();
  }

  /** @param {string} uk */
  resizeTo(uk) {
    this.resizeInput.checked = true;
    this.select.value = uk;
    this.sync();
  }
}

class WjNecklaceLengths extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.addEventListener('click', (event) => {
      const button = /** @type {HTMLElement} */ (event.target).closest('button[data-length]');
      if (button) this.show(button.dataset.length);
    });
    this.show(this.querySelector('[aria-pressed="true"]')?.dataset.length || '45');
  }

  /** @param {string} length */
  show(length) {
    this.querySelectorAll('[data-length]').forEach((el) => {
      const active = el.dataset.length === length;
      el.classList.toggle('is-active', active);
      if (el.tagName === 'BUTTON') el.setAttribute('aria-pressed', String(active));
    });
  }
}

if (!customElements.get('wj-tabs')) customElements.define('wj-tabs', WjTabs);
if (!customElements.get('wj-size-calc')) customElements.define('wj-size-calc', WjSizeCalc);
if (!customElements.get('wj-custom-size')) customElements.define('wj-custom-size', WjCustomSize);
if (!customElements.get('wj-necklace-lengths')) customElements.define('wj-necklace-lengths', WjNecklaceLengths);
